import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import {
  RoleSalaryRangesPageDocument,
  SalaryRangesTableRowRoleFragment,
} from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DraftSalaryRangeModal from 'modules/job_catalog/components/DraftSalaryRangeModal'
import SalaryRangesTable from 'modules/job_catalog/components/SalaryRangesTable'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import { RoleTabs } from 'modules/job_catalog/pages/Role/RoleTabs'

interface OutletContext {
  role: Role
  canEditRole: boolean
  isLoading: boolean
}

const Component = () => {
  const { role, isLoading, canEditRole } = useOutletContext<OutletContext>()
  const { data, refetch } = useQuery(RoleSalaryRangesPageDocument, {
    variables: { id: role.id },
  })
  const [isDraftSalaryOpen, toggleDraftModal] = useToggle(false)
  const [draftSalaryRange, setDraftSalaryRange] =
    useState<SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'][number]>()

  const handleNewSalaryRangeClick = () => {
    track(trackingEvents.jobCatalog.createJobLevelSalaryRangeStart)
    toggleDraftModal(true)()
  }

  const handleEditSalaryRange = (
    salaryRange: SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'][number]
  ) => {
    setDraftSalaryRange(salaryRange)
    toggleDraftModal(true)()
  }

  const handleSuccess = () => {
    refetch()
    toggleDraftModal(false)()
  }

  const handleClose = () => {
    toggleDraftModal(false)()
    setDraftSalaryRange(undefined)
  }

  return (
    <AuthorizeUser authorizeFn={() => canEditRole || isLoading}>
      <Box width="full" gap="s24" alignItems="flexEnd">
        <RoleTabs roleId={role.id} />

        <Button type="secondary" iconName="add" onClick={handleNewSalaryRangeClick}>
          {i18n.t('job_catalog.salary_ranges.new_salary_range')}
        </Button>

        <SalaryRangesTable
          levels={data?.jobCatalog.role?.levels}
          legalEntities={data?.companies.legalEntitiesConnection.nodes}
          onEdit={handleEditSalaryRange}
          onDeleted={refetch}
        />
      </Box>

      {isDraftSalaryOpen && role && (
        <DraftSalaryRangeModal
          roleId={role.id}
          roleName={role.name}
          draft={draftSalaryRange}
          levels={data?.jobCatalog.role?.levels}
          roleLegalEntities={data?.jobCatalog.role?.legalEntities}
          legalEntities={data?.companies.legalEntitiesConnection.nodes}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </AuthorizeUser>
  )
}

export default Component
